<template>
	<div>
		<b-alert variant="danger" :show="!!error" class="mb-3">{{error}}</b-alert>
		<b-form-row>
			<b-col cols="12" md="6" xl="4" class="mb-3">
				<b-card no-body class="mb-3 h-100" header="Value based gift alerts">
					<template #footer>
						<div class="text-center">
							<b-button variant="primary" class="my-n1" size="sm" @click="editThreshold()">Add threshold</b-button>
						</div>
					</template>
					<b-list-group flush class="flex-fill" v-if="loading>0">
						<b-list-group-item v-for="idx in 5" :key="idx">
							<b-skeleton :width="`${70 + (idx % 3) * 10}%`"></b-skeleton>
						</b-list-group-item>
					</b-list-group>
					<b-list-group flush class="flex-fill" v-else>
						<b-list-group-item class="d-flex" style="gap: .5em">
							<a href="#" @click="playTestAlert('gift')" v-b-tooltip.hover.left title="Play test alert"><b-icon icon="play-fill"></b-icon></a>
							<div class="flex-fill">
								Gifts from {{generateValueString(0, $parent.$parent.user.currency_code)}}
							</div>
							<span v-b-tooltip.hover.left.v-info v-if="giftEventType?.sounds?.length > 0 || giftEventType?.images?.length > 0" title="Has media configured"><b-icon icon="music-note-beamed" class="text-muted"></b-icon></span>
							<a href="#" @click="$refs.soundModal.show('gift')" v-b-tooltip.hover.right title="Edit alert media"><b-icon icon="pencil-fill"></b-icon></a>
						</b-list-group-item>
						<b-list-group-item v-for="(a, idx) in giftThresholds" :key="idx" class="d-flex" style="gap: .5em">
							<a href="#" @click="playTestAlert(a.type)" v-b-tooltip.hover.left title="Play test alert"><b-icon icon="play-fill"></b-icon></a>
							<div class="flex-fill">
								{{`From ${generateValueString(a.amount, $parent.$parent.user.currency_code)}${idx &lt; giftThresholds.length - 1 ? ` to under ${generateValueString(giftThresholds[idx+1].amount, $parent.$parent.user.currency_code)}` : ''}`}}
							</div>
							<span v-if="a.files?.length > 0" v-b-tooltip.hover.left.v-info title="Has media configured"><b-icon icon="music-note-beamed" class="text-muted"></b-icon></span>
							<a href="#" @click="$bvModal.msgBoxConfirm('Are you sure you want to remove this threshold?', { title: 'Please Confirm', size: 'sm', buttonSize: 'sm', okVariant: 'danger', footerClass: 'p-2', hideHeaderClose: false, centered: true }).then(value => value&&removeThreshold(a.id))" v-b-tooltip.hover.left.v-danger title="Remove gift threshold" class="text-danger"><b-icon icon="trash-fill"></b-icon></a>
							<a href="#" @click="editThreshold(a.id)" v-b-tooltip.hover.right title="Edit gift alert threshold"><b-icon icon="pencil-fill"></b-icon></a>
						</b-list-group-item>
					</b-list-group>
				</b-card>
			</b-col>
			<b-col cols="12" md="6" xl="4" class="mb-3">
				<b-card header="Alerts" no-body class="h-100">
					<b-list-group flush class="flex-fill" v-if="loading>0">
						<b-list-group-item v-for="idx in 8" :key="idx">
							<b-skeleton :width="`${70 + (idx % 3) * 10}%`"></b-skeleton>
						</b-list-group-item>
					</b-list-group>
					<b-list-group flush v-else>
						<b-list-group-item v-for="(a, idx) in alertTypes" :key="idx" class="d-flex" style="gap: .5em">
							<a href="#" @click="playTestAlert(a.event_type)" v-b-tooltip.hover.left title="Play test alert"><b-icon icon="play-fill"></b-icon></a>
							<div class="flex-fill">{{a.event_name}}</div>
							<span v-b-tooltip.hover.left.v-info v-if="a.sounds?.length > 0 || a.images?.length > 0" title="Has media configured"><b-icon icon="music-note-beamed" class="text-muted"></b-icon></span>
							<a href="#" @click="$refs.soundModal.show(a.event_type)" v-b-tooltip.hover.right title="Edit alert media"><b-icon icon="pencil-fill"></b-icon></a>
						</b-list-group-item>
					</b-list-group>
				</b-card>
			</b-col>
			<b-col cols="12" md="6" xl="4" class="mb-3">
				<b-card header="Non-alert events" no-body class="h-100">
					<b-list-group flush class="flex-fill" v-if="loading>0">
						<b-list-group-item v-for="idx in 12" :key="idx">
							<b-skeleton :width="`${70 + (idx % 3) * 10}%`"></b-skeleton>
						</b-list-group-item>
					</b-list-group>
					<b-list-group flush v-else>
						<b-list-group-item v-for="(a, idx) in eventTypes" :key="idx" class="d-flex" style="gap: .5em">
							<a href="#" @click="playTestAlert(a.event_type)" v-b-tooltip.hover.left title="Play test alert"><b-icon icon="play-fill"></b-icon></a>
							<div class="flex-fill">{{a.event_name}}</div>
							<span v-b-tooltip.hover.left.v-info v-if="a.sounds?.length > 0 || a.images?.length > 0" title="Has media configured"><b-icon icon="music-note-beamed" class="text-muted"></b-icon></span>
							<a href="#" @click="$refs.soundModal.show(a.event_type)" v-b-tooltip.hover.right title="Edit event media" size="sm"><b-icon icon="pencil-fill"></b-icon></a>
						</b-list-group-item>
					</b-list-group>
				</b-card>
			</b-col>
		</b-form-row>
		<sound-modal ref="soundModal" @saved="reloadUserEventTypes()"></sound-modal>
		<b-modal ref="thresholdEditModal" title="Edit gift alert threshold">
			<b-form @submit.stop.prevent="saveThreshold(); return false" id="towerEditForm" v-if="curGiftThreshold">
				<b-form-row>
					<b-col>
						<b-form-group label="Minimum">
							<b-form-input v-model.number="curGiftThreshold.amount" required/>
						</b-form-group>
					</b-col>
				</b-form-row>
				<b-form-row>
					<b-col>
						<b-form-group label="Media" description="Sounds, images and video">
							<pipi-pupu-multi-file v-model="curGiftThreshold.files" accept="image/*,audio/*,video/*" />
						</b-form-group>
					</b-col>
				</b-form-row>
			</b-form>
			<template #modal-footer="{cancel}">
				<div class="w-100">
					<b-alert variant="danger" :show="!!saveError">{{saveError}}</b-alert>
					<div class="float-left" v-if="curGiftThreshold?.id">
						<a href="#" @click.prevent.stop="showDeleteModal()" class="text-danger"><b-icon icon="trash"></b-icon> Delete</a>
					</div>
					<div class="float-right">
						<b-spinner v-show="saving > 0" small label="Spinning" class="mr-3"></b-spinner>
						<b-button variant="success" type="submit" form="towerEditForm" class="mr-2">
							Save
						</b-button>
						<b-button @click="cancel()">
							Cancel
						</b-button>
					</div>
				</div>
			</template>
		</b-modal>
	</div>
</template>

<script>
import SoundModal from '@/views/Widgets/Modal/Sound.vue';
import util from '@/util.js';
import PipiPupuMultiFile from '@/components/PipiPupuMultiFile.vue';

export default {
	data() { return {
		loading: 0,
		error: null,
		alertTypes: [],
		eventTypes: [],
		giftEventType: null,
		giftThresholds: [],
		curGiftThreshold: null,
		saving: 0,
		saveError: null,
	}; },
	components: {
		SoundModal,
		PipiPupuMultiFile,
	},
	async mounted() {
		try {
			this.loading++;
			this.error = null;
			this.saveError = null;
			this.giftThresholds = await this.$api.get('giftThresholds');
			await this.loadUserEventTypes();
		} catch(e) {
			console.error(e);
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	methods: {
		async loadUserEventTypes() {
			const eventTypes = await this.$api.get('userEventTypes');
			this.alertTypes = eventTypes.filter(t => t.event_alert_type == 'alert' && t.event_type != 'gift');
			this.giftEventType = eventTypes.find(t => t.event_type == 'gift');
			this.eventTypes = eventTypes.filter(t => t.event_alert_type == 'event');
		},
		async reloadUserEventTypes() {
			try {
				this.loading++;
				this.error = null;
				await this.loadUserEventTypes();
			} catch(e) {
				console.error(e);
				this.error = e;
			} finally {
				this.loading--;
			}
		},
		generateValueString(value, currency) {
			return util.generateValueString(value, currency);
		},
		async editThreshold(id) {
			this.$refs.thresholdEditModal.show();
			if(id) {
				this.curGiftThreshold = await this.$api.get(`giftThreshold/${id}`);
			} else {
				const amount = (Math.max(0, ...this.giftThresholds.map(t => t.amount)) || 0) + 5;
				this.curGiftThreshold = {
					amount,
					files: [],
				};
			}
		},
		async reloadGiftThresholds() {
			try {
				this.loading++;
				this.error = null;
				this.giftThresholds = await this.$api.get('giftThresholds');
			} catch(e) {
				console.error(e);
				this.error = e;
			} finally {
				this.loading--;
			}
		},
		async saveThreshold() {
			try {
				this.saving++;
				this.saveError = null;
				if(this.curGiftThreshold.id)
					await this.$api.patch(`giftThreshold/${encodeURIComponent(this.curGiftThreshold.id)}`, this.curGiftThreshold);
				else
					await this.$api.post(`giftThreshold`, this.curGiftThreshold);
				this.$refs.thresholdEditModal.hide();
				this.reloadGiftThresholds();
			} catch(e) {
				console.error(e);
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
		async removeThreshold(id) {
			try {
				this.loading++;
				this.error = null;
				await this.$api.delete(`giftThreshold/${id}`);
				this.reloadGiftThresholds();
			} catch(e) {
				console.error(e);
				this.error = e;
			} finally {
				this.loading--;
			}
		},
	},
};
</script>
